import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { verifyCertificate } from "@/services/certificateService";
import verifyNFTCertificate from "@/services/nftCertificateService";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle, XCircle, ExternalLink, Globe, Image } from "lucide-react";
import { SEO } from "@/components/ui/seo";
import { useToast } from "@/components/ui/use-toast";

const VerifyCertificate: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [certificateId, setCertificateId] = useState("");
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");
  const [isNFTVerification, setIsNFTVerification] = useState(false);

  const handleVerify = async () => {
    if (!certificateId.trim()) {
      toast({
        title: t("certificate.verify.idRequired"),
        description: t("certificate.verify.enterCertificateId"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setVerificationResult(null);

    try {
      // Gọi API xác thực certificate
      const response = await fetch(`/api/certificates/verify/${certificateId}`);
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || t("certificate.verify.notFound"));
      }

      // Nếu là NFT certificate và có địa chỉ ví, xác thực trên blockchain
      if (data.isNFT && isNFTVerification && walletAddress) {
        try {
          const blockchainVerification =
            await verifyNFTCertificate.verifyNFTCertificate(
              certificateId,
              walletAddress,
            );

          // Kết hợp kết quả từ backend và blockchain
          setVerificationResult({
            ...data,
            blockchainVerified: blockchainVerification.isValid,
            blockchainData: blockchainVerification.isValid
              ? blockchainVerification.certificate
              : null,
          });
        } catch (blockchainError) {
          console.error("Blockchain verification error:", blockchainError);

          // Vẫn hiển thị kết quả từ backend nếu xác thực blockchain thất bại
          setVerificationResult({
            ...data,
            blockchainVerified: false,
            blockchainError: blockchainError.message,
          });
        }
      } else {
        // Certificate thông thường hoặc không xác thực trên blockchain
        setVerificationResult(data);
      }
    } catch (error) {
      console.error("Verification error:", error);
      setVerificationResult({
        success: false,
        message: error.message || t("certificate.verify.verificationFailed"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Render kết quả xác thực
  const renderVerificationResult = () => {
    if (!verificationResult) return null;

    if (!verificationResult.success) {
      return (
        <Alert variant="destructive" className="mt-4">
          <XCircle className="h-5 w-5 mr-2" />
          <AlertTitle>{t("certificate.verify.invalid")}</AlertTitle>
          <AlertDescription>
            {verificationResult.message || t("certificate.verify.notFound")}
          </AlertDescription>
        </Alert>
      );
    }

    const { certificate, isNFT } = verificationResult;

    return (
      <Card className="mt-6 border-2 border-green-500">
        <CardHeader className="bg-green-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-green-700 flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                {t("certificate.verify.valid")}
              </CardTitle>
              <CardDescription>
                {t("certificate.verify.certificateDetails")}
              </CardDescription>
            </div>
            {isNFT && (
              <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium flex items-center">
                <Globe className="h-4 w-4 mr-1" />
                NFT Certificate
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            <div>
              <span className="font-medium">{t("certificate.id")}:</span>{" "}
              {certificate.certificate_id}
            </div>
            <div>
              <span className="font-medium">{t("certificate.course")}:</span>{" "}
              {certificate.course_name}
            </div>
            <div>
              <span className="font-medium">{t("certificate.recipient")}:</span>{" "}
              {certificate.recipient_name}
            </div>
            <div>
              <span className="font-medium">{t("certificate.issueDate")}:</span>{" "}
              {new Date(certificate.issue_date).toLocaleDateString()}
            </div>

            {isNFT && (
              <>
                <hr className="my-3" />
                <div className="font-medium text-blue-700">
                  {t("certificate.blockchainInfo")}
                </div>

                {verificationResult.blockchainVerified === false &&
                  !verificationResult.blockchainError && (
                    <Alert variant="warning" className="mt-2">
                      <AlertTitle>
                        {t("certificate.verify.blockchainWarning")}
                      </AlertTitle>
                      <AlertDescription>
                        {t("certificate.verify.blockchainVerificationFailed")}
                      </AlertDescription>
                    </Alert>
                  )}

                {verificationResult.blockchainError && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertTitle>
                      {t("certificate.verify.blockchainError")}
                    </AlertTitle>
                    <AlertDescription>
                      {verificationResult.blockchainError}
                    </AlertDescription>
                  </Alert>
                )}

                <div>
                  <span className="font-medium">
                    {t("certificate.network")}:
                  </span>{" "}
                  {certificate.blockchain_network === "polygon"
                    ? "Polygon"
                    : certificate.blockchain_network}
                </div>

                {certificate.nft_token_id && (
                  <div>
                    <span className="font-medium">Token ID:</span>{" "}
                    {certificate.nft_token_id}
                  </div>
                )}

                {certificate.recipient_wallet_address && (
                  <div>
                    <span className="font-medium">
                      {t("certificate.ownerAddress")}:
                    </span>{" "}
                    <span className="font-mono text-sm">
                      {certificate.recipient_wallet_address.substring(0, 10)}...
                      {certificate.recipient_wallet_address.substring(
                        certificate.recipient_wallet_address.length - 8,
                      )}
                    </span>
                  </div>
                )}

                {certificate.nft_transaction_hash && (
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-blue-600"
                      onClick={() =>
                        window.open(
                          certificate.blockchain_verification_url ||
                            `https://polygonscan.com/tx/${certificate.nft_transaction_hash}`,
                          "_blank",
                        )
                      }
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      {t("certificate.viewOnBlockchain")}
                    </Button>
                  </div>
                )}

                {!certificate.nft_transaction_hash &&
                  certificate.blockchain_verification_url && (
                    <div className="mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-blue-600"
                        onClick={() =>
                          window.open(
                            certificate.blockchain_verification_url,
                            "_blank",
                          )
                        }
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        {t("certificate.viewOnBlockchain")}
                      </Button>
                    </div>
                  )}
              </>
            )}
          </div>
        </CardContent>
        <CardFooter className="border-t bg-gray-50 flex justify-between">
          <div className="text-sm text-gray-500">
            {t("certificate.verify.verifiedAt")} {new Date().toLocaleString()}
          </div>

          {verificationResult.isNFT && certificate.image_url && (
            <Button
              variant="ghost"
              size="sm"
              className="text-blue-600"
              onClick={() => window.open(certificate.image_url, "_blank")}
            >
              <Image className="h-4 w-4 mr-1" />
              {t("certificate.viewImage")}
            </Button>
          )}
        </CardFooter>
      </Card>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <SEO
        title={t("certificate.verify.pageTitle") + " | Giác Ngộ"}
        description={t("certificate.verify.description", "Xác minh tính hợp lệ của chứng chỉ khóa học được cấp bởi Giác Ngộ - Nền tảng học Phật giáo trực tuyến")}
        ogType="website"
      />

      <h1 className="text-3xl font-bold mb-6 text-center">
        {t("certificate.verify.title")}
      </h1>

      <Card>
        <CardHeader>
          <CardTitle>{t("certificate.verify.checkValidity")}</CardTitle>
          <CardDescription>
            {t("certificate.verify.enterIdBelow")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("certificate.id")}
              </label>
              <Input
                placeholder={t("certificate.verify.idPlaceholder")}
                value={certificateId}
                onChange={(e) => setCertificateId(e.target.value)}
              />
            </div>

            <div className="flex items-center space-x-2 mt-4">
              <input
                type="checkbox"
                id="nftVerification"
                checked={isNFTVerification}
                onChange={(e) => setIsNFTVerification(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="nftVerification" className="text-sm">
                {t("certificate.verify.checkBlockchain")}
              </label>
            </div>

            {isNFTVerification && (
              <div className="mt-2">
                <label className="block text-sm font-medium mb-1">
                  {t("certificate.ownerAddress")}
                </label>
                <Input
                  placeholder={t("certificate.verify.walletAddressPlaceholder")}
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t("certificate.verify.walletAddressHint")}
                </p>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter>
          <Button
            onClick={handleVerify}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading
              ? t("common.loading")
              : t("certificate.verify.verifyButton")}
          </Button>
        </CardFooter>
      </Card>

      {renderVerificationResult()}

      <div className="mt-8 text-center text-sm text-gray-500">
        <p>{t("certificate.verify.helpText")}</p>
        <p className="mt-2">
          {t("certificate.verify.contactUs")}{" "}
          <a
            href="mailto:support@giacngo.org"
            className="text-blue-600 hover:underline"
          >
            support@giacngo.org
          </a>
        </p>
      </div>
    </div>
  );
};

export default VerifyCertificate;
